/**
 * KodW Buurtbasis - Sturnia Integration Module
 * =============================================
 *
 * Shared sturnia ecosystem initialization for all KodW apps.
 * This module handles:
 * - Auto-initialization of sturnia-base, sturnia-plot, sturnia-legend
 * - KodW color palette registration
 * - Legend event handlers
 *
 * Apps should import this module and add their own store factories.
 */

// ============================================================================
// Auto Imports (side-effect initialization)
// ============================================================================

// Import auto modules which:
// - Register libraries on window.Sturnia
// - Initialize AutoScanner for data-starling-* attributes
// - Set up event handlers for initialization
import "@starling-cloud/sturnia-base/auto";
import "@starling-cloud/sturnia-plot/auto";
// Note: sturnia-legend/auto is imported by sturnia-plot/auto

// ============================================================================
// Re-exports for Apps
// ============================================================================

import {
    Store,
    store as getStore,
    globalStores,
} from "@starling-cloud/sturnia-base";
import { ColorPalettes } from "@starling-cloud/sturnia-plot";

export { ColorPalettes, Store, getStore, globalStores };

// ============================================================================
// KodW Color Palette
// ============================================================================

// Register Kijk op de Wijk habitat colors
// Ordered for good contrast between adjacent slices in donut charts
ColorPalettes.register(
    "kodw",
    [
        "#009ee0", // Sky (cool blue)
        "#ebd22e", // Bee (warm yellow)
        "#004c2e", // Tree (cool dark green)
        "#de78ae", // People (warm pink)
        "#005496", // Water (cool blue - VORM)
        "#3fa535", // Grass (warm-ish green)
    ],
    "Kijk op de Wijk habitat colors"
);

// ============================================================================
// Legend Event Handlers
// ============================================================================

/**
 * Set up legend click handling for all plots.
 * When a legend item is clicked, highlight the corresponding plot segment.
 */
function setupLegendInteraction(): void {
    document.addEventListener("starling:legend:click", (event: Event) => {
        const detail = (event as CustomEvent).detail;
        const { plotId, itemId, active } = detail;

        const plotElement = document.getElementById(plotId);
        if (plotElement) {
            plotElement.dispatchEvent(
                new CustomEvent("sturnia:highlight", {
                    detail: { itemId, active },
                })
            );
        }
    });

    document.addEventListener("starling:legend:hover", (event: Event) => {
        const detail = (event as CustomEvent).detail;
        const { plotId, itemId, hovering } = detail;

        const plotElement = document.getElementById(plotId);
        if (plotElement) {
            plotElement.dispatchEvent(
                new CustomEvent("sturnia:hover", {
                    detail: { itemId, hovering },
                })
            );
        }
    });
}

// ============================================================================
// Store Factory Helper
// ============================================================================

/**
 * Create a data Store for a specific study and endpoint.
 * Generic factory usable by all apps.
 *
 * @param studyUuid - The study UUID
 * @param endpoint - The API endpoint name (e.g., "demographics", "frequency")
 * @param refreshInterval - Optional refresh interval in ms
 * @returns Store instance subscribed to the API endpoint
 */
export function createStudyStore(
    studyUuid: string,
    endpoint: string,
    refreshInterval?: number
): Store<unknown> {
    const storeName = `study_${studyUuid}_${endpoint}`;

    const existing = globalStores.get<unknown>(storeName);
    if (existing) {
        return existing;
    }

    return globalStores.getOrCreate<unknown>(storeName, {
        url: `/api/study/${studyUuid}/${endpoint}/`,
        refreshInterval,
    });
}

// ============================================================================
// Initialization
// ============================================================================

/**
 * Initialize stores from data attributes on page.
 * Looks for elements with data-sturnia-store-study attribute
 * and creates stores for them.
 */
export function initializeStoresFromDOM(): void {
    const storeElements = document.querySelectorAll(
        "[data-sturnia-store-study]"
    );

    storeElements.forEach((element) => {
        const studyUuid = element.getAttribute("data-sturnia-store-study");
        const storeType = element.getAttribute("data-sturnia-store-type");
        if (studyUuid && storeType) {
            createStudyStore(studyUuid, storeType);
        }
    });
}

// Run setup on DOM ready
if (typeof document !== "undefined") {
    if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", () => {
            setupLegendInteraction();
            initializeStoresFromDOM();
        });
    } else {
        setupLegendInteraction();
        initializeStoresFromDOM();
    }
}

console.log("[kodw] Buurtbasis Sturnia integration initialized");
